import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircleIcon, ArrowRightIcon, ClockIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import crmApi from '../../utils/crmApi';

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loadSubscriptionData } = useSubscription();
  const sessionId = searchParams.get('session_id');
  const [verificationStatus, setVerificationStatus] = useState('checking'); // 'checking', 'active', 'processing', 'error'
  const retryCountRef = useRef(0);

  const verifySubscription = useCallback(async () => {
    if (!sessionId) return;
    
    try {
      const res = await crmApi.get('/subscription/verify-session', {
        params: { session_id: sessionId }
      });

      if (res.data?.success) {
        if (res.data.isUpgraded) {
          // Subscription is active in database
          setVerificationStatus('active');
          loadSubscriptionData();
          toast.success('Subscription activated successfully!');
        } else if (res.data.processing) {
          // Payment successful but webhook still processing
          setVerificationStatus('processing');
          // Retry after 2 seconds (up to 5 times)
          if (retryCountRef.current < 5) {
            retryCountRef.current += 1;
            setTimeout(() => {
              verifySubscription();
            }, 2000);
          } else {
            setVerificationStatus('error');
            toast.error('Subscription is processing. Please refresh the page in a few moments.');
          }
        } else {
          setVerificationStatus('error');
          toast.error('Unable to verify subscription status');
        }
      }
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationStatus('error');
      toast.error('Failed to verify subscription');
    }
  }, [sessionId, loadSubscriptionData]);

  useEffect(() => {
    if (sessionId) {
      verifySubscription();
    }
  }, [sessionId, verifySubscription]);

  const getStatusContent = () => {
    if (verificationStatus === 'checking' || verificationStatus === 'processing') {
      return (
        <>
          <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
            <ClockIcon className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-foreground">
              Processing Subscription...
            </h2>
            <p className="text-muted-foreground">
              {verificationStatus === 'checking' 
                ? 'Verifying your subscription...'
                : 'Your payment was successful! We\'re activating your subscription. This may take a few moments.'}
            </p>
          </div>
        </>
      );
    }

    if (verificationStatus === 'error') {
      return (
        <>
          <div className="mx-auto w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
            <ClockIcon className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-foreground">
              Payment Received
            </h2>
            <p className="text-muted-foreground">
              Your payment was successful! Your subscription is being activated. Please refresh the page in a few moments or check your subscription status.
            </p>
          </div>
        </>
      );
    }

    // verificationStatus === 'active'
    return (
      <>
        <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
          <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-foreground">
            Subscription Activated!
          </h2>
          <p className="text-muted-foreground">
            Thank you for subscribing. Your plan is now active and you have full access to all features.
          </p>
        </div>
      </>
    );
  };

  return (
    <div className="flex items-center justify-center min-h-[500px] p-6">
      <Card className="max-w-md w-full p-8 text-center space-y-6">
        {getStatusContent()}

        <div className="flex flex-col gap-3 pt-4">
          <Button
            onClick={() => navigate('/dashboard')}
            className="w-full"
          >
            Go to Dashboard
            <ArrowRightIcon className="h-4 w-4 ml-2" />
          </Button>
          <Button
            onClick={() => navigate('/subscription')}
            variant="outline"
            className="w-full"
          >
            View Subscription Details
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default SubscriptionSuccessPage;

